import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FiArrowLeft, FiArrowRight, FiCheck, FiPause, FiPlay, FiX } from 'react-icons/fi';
import './tour.css';

const PAD = 12;          // spotlight padding around the target
const GAP = 16;          // distance between spotlight and popover
const HEADER = 76;       // default fixed-header height to keep targets clear of it
const POPOVER_H = 240;   // approximate popover height used for placement
const AUTOPLAY_MS = 6500;

function findTarget(name) {
  return document.querySelector(`[data-tour="${name}"]`);
}

// Scroll so the target sits just under the header, or centered when it fits.
function scrollToTarget(el, HEADER) {
  const rect = el.getBoundingClientRect();
  const viewH = window.innerHeight;
  const fits = rect.height + PAD * 2 + POPOVER_H + GAP < viewH - HEADER;
  const offset = fits ? Math.max(HEADER + 24, (viewH - rect.height - POPOVER_H) / 2) : HEADER + 16;
  window.scrollTo({ top: window.scrollY + rect.top - offset, behavior: 'smooth' });
}

function measure(el, HEADER) {
  const r = el.getBoundingClientRect();
  const viewH = window.innerHeight;
  const viewW = window.innerWidth;
  const top = Math.max(r.top - PAD, HEADER - 8);
  const left = Math.max(r.left - PAD, 8);
  const bottom = Math.min(r.bottom + PAD, viewH - 8);
  const right = Math.min(r.right + PAD, viewW - 8);
  return { top, left, width: Math.max(right - left, 0), height: Math.max(bottom - top, 0) };
}

function placePopover(spot, HEADER) {
  const viewH = window.innerHeight;
  const viewW = window.innerWidth;
  const width = Math.min(380, viewW - 32);
  const left = Math.min(Math.max(spot.left + spot.width / 2 - width / 2, 16), viewW - width - 16);

  if (viewW < 640) return { mode: 'dock', width };
  if (viewH - (spot.top + spot.height) > POPOVER_H + GAP) {
    return { mode: 'below', top: spot.top + spot.height + GAP, left, width };
  }
  if (spot.top - HEADER > POPOVER_H + GAP) {
    return { mode: 'above', top: spot.top - GAP, left, width };
  }
  // Beside the target when it leaves enough room on one side.
  const sideTop = Math.min(Math.max(spot.top + spot.height / 2 - POPOVER_H / 2, HEADER + 8), viewH - POPOVER_H - 16);
  if (spot.left - 16 >= width + GAP) {
    return { mode: 'side', top: sideTop, left: spot.left - GAP - width, width };
  }
  if (viewW - (spot.left + spot.width) - 16 >= width + GAP) {
    return { mode: 'side', top: sideTop, left: spot.left + spot.width + GAP, width };
  }
  return { mode: 'dock', width };
}

export default function TourMode({ steps: allSteps, onClose, topOffset = HEADER }) {
  // Skip steps whose target isn't on the page (e.g. data that hasn't loaded). Checked after
  // the first commit so anything the page reveals for the tour (like an expanded sidebar) counts.
  const [found, setFound] = useState(null);
  useLayoutEffect(() => {
    setFound(allSteps.filter((s) => findTarget(s.target)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const steps = found || [];
  const [index, setIndex] = useState(0);
  const [spot, setSpot] = useState(null);
  const [autoplay, setAutoplay] = useState(false);
  const frame = useRef(0);
  const nextBtn = useRef(null);

  const step = steps[index] || { target: '' };
  const isLast = index >= steps.length - 1;

  // Nothing to show: close instead of leaving an empty dimmed overlay.
  useEffect(() => {
    if (found && found.length === 0) onClose();
  }, [found, onClose]);

  const update = useCallback(() => {
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      const el = findTarget(step.target);
      setSpot(el ? measure(el, topOffset) : null);
    });
  }, [step.target, topOffset]);

  // Move to the new step: scroll there, then keep the spotlight glued to it.
  useLayoutEffect(() => {
    const el = findTarget(step.target);
    if (!el) {
      setSpot(null); // target vanished mid-tour: keep the card, docked, without a spotlight
      return undefined;
    }
    scrollToTarget(el, topOffset);
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      cancelAnimationFrame(frame.current);
    };
  }, [step.target, topOffset, update]);

  const next = useCallback(() => {
    if (isLast) onClose();
    else setIndex((i) => i + 1);
  }, [isLast, onClose]);
  const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') back();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, back, onClose]);

  useEffect(() => {
    if (!autoplay) return undefined;
    if (isLast) { setAutoplay(false); return undefined; }
    const t = setTimeout(next, AUTOPLAY_MS);
    return () => clearTimeout(t);
  }, [autoplay, index, isLast, next]);

  useEffect(() => { nextBtn.current?.focus({ preventScroll: true }); }, [index]);

  if (steps.length === 0) return null;

  const pop = spot
    ? placePopover(spot, topOffset)
    : { mode: 'dock', width: Math.min(380, window.innerWidth - 32) };
  const popStyle = !pop ? undefined
    : pop.mode === 'dock' ? { width: pop.width }
    : { top: pop.top, left: pop.left, width: pop.width };

  return (
    <div className="nb-tour" role="dialog" aria-modal="true" aria-labelledby="nb-tour-title">
      <div className="nb-tour-blocker" />
      {spot && (
        <div
          className="nb-tour-spot"
          style={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height }}
        />
      )}

      {pop && (
        <div className={`nb-tour-pop is-${pop.mode}`} style={popStyle}>
          <div className="nb-tour-progress"><span style={{ width: `${((index + 1) / steps.length) * 100}%` }} /></div>
          <div className="nb-tour-meta">
            <span className="nb-tour-badge">Tour mode</span>
            <span className="nb-tour-count">Step {index + 1} of {steps.length}</span>
            <button type="button" className="nb-tour-icon" onClick={onClose} aria-label="Exit tour"><FiX /></button>
          </div>

          <h3 id="nb-tour-title">{step.title}</h3>
          <p>{step.body}</p>

          <div className="nb-tour-dots" role="tablist" aria-label="Tour steps">
            {steps.map((s, i) => (
              <button
                key={s.target}
                type="button"
                role="tab"
                aria-selected={i === index}
                aria-label={`Go to step ${i + 1}: ${s.title}`}
                className={i === index ? 'is-active' : i < index ? 'is-done' : ''}
                onClick={() => setIndex(i)}
              />
            ))}
          </div>

          <div className="nb-tour-actions">
            <button
              type="button"
              className="nb-tour-icon"
              onClick={() => setAutoplay(!autoplay)}
              aria-label={autoplay ? 'Pause auto-play' : 'Auto-play tour'}
              title={autoplay ? 'Pause auto-play' : 'Auto-play'}
            >
              {autoplay ? <FiPause /> : <FiPlay />}
            </button>
            <div className="nb-tour-nav">
              {index > 0 && (
                <button type="button" className="nb-tour-btn" onClick={back}><FiArrowLeft /> Back</button>
              )}
              <button type="button" ref={nextBtn} className="nb-tour-btn is-primary" onClick={next}>
                {isLast ? <>Finish <FiCheck /></> : <>Next <FiArrowRight /></>}
              </button>
            </div>
          </div>
          {autoplay && <div key={index} className="nb-tour-timer" style={{ animationDuration: `${AUTOPLAY_MS}ms` }} />}
        </div>
      )}
    </div>
  );
}
